/* PublicShare — page publique d'un lien de partage (/share/:token).
 * Sans compte : consultation du document dans le navigateur, avec signature
 * électronique du NCNDA au préalable si le lien est protégé.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Download, FileText, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export default function PublicShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [accessKey, setAccessKey] = useState(sessionStorage.getItem(`share_key_${token}`) || "");
  const [signing, setSigning] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", accept: false });

  // Vérifie que la clé locale est toujours valide (clé périmée, re-signature ailleurs…)
  useEffect(() => {
    if (!meta || !meta.require_nda || !accessKey) return;
    fetch(`${API}/api/public/share/${token}/file?key=${encodeURIComponent(accessKey)}`, { method: "HEAD" })
      .then((r) => {
        if (r.status === 403) {
          sessionStorage.removeItem(`share_key_${token}`);
          setAccessKey("");
        }
      })
      .catch(() => {});
  }, [meta, accessKey, token]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/share/${token}`);
        const data = await r.json();
        if (!r.ok) throw new Error(data?.detail || "Lien invalide.");
        setMeta(data);
      } catch (e) { setError(e.message); }
      setLoading(false);
    })();
  }, [token]);

  const sign = async (e) => {
    e.preventDefault();
    if (!form.accept) return;
    setSigning(true);
    try {
      const r = await fetch(`${API}/api/public/share/${token}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Signature impossible.");
      sessionStorage.setItem(`share_key_${token}`, data.access_key);
      setAccessKey(data.access_key);
    } catch (e2) { setError(e2.message); }
    setSigning(false);
  };

  const fileUrl = (dl = false) =>
    `${API}/api/public/share/${token}/file?${accessKey ? `key=${encodeURIComponent(accessKey)}&` : ""}${dl ? "download=true" : ""}`;

  const canView = meta && (!meta.require_nda || !!accessKey);
  const previewable = meta && (meta.mime_type?.startsWith("image/") || meta.mime_type === "application/pdf");

  return (
    <div className="min-h-screen bg-[#0A0E13] text-zinc-100 flex flex-col" data-testid="public-share-root">
      <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#378ADD]">Document partagé</div>
          <div className="text-sm font-semibold">{meta?.org_name || "Constant Partners"}</div>
        </div>
        {meta && (
          <div className="text-right">
            <div className="text-xs text-zinc-300 font-medium" data-testid="public-share-docname">{meta.doc_name}</div>
            <div className="text-[10px] text-zinc-500">{fmtBytes(meta.size_bytes)}{meta.expires_at ? ` · expire le ${meta.expires_at.slice(0, 10)}` : ""}</div>
          </div>
        )}
      </header>

      <main className="flex-1 flex flex-col min-h-0">
        {loading && (
          <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}

        {!loading && error && (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 p-6" data-testid="public-share-error">
            <AlertTriangle className="w-10 h-10 text-[#EF9F27]" />
            <div className="text-sm text-zinc-300">{error}</div>
          </div>
        )}

        {!loading && !error && meta && !canView && (
          <div className="flex-1 flex items-start justify-center p-6 overflow-y-auto">
            <div className="w-full max-w-2xl space-y-4" data-testid="public-share-nda">
              <div className="flex items-center gap-2 text-[#1D9E75]">
                <ShieldCheck className="w-5 h-5" />
                <h1 className="text-lg font-semibold text-zinc-100">Accord de confidentialité requis</h1>
              </div>
              <p className="text-xs text-zinc-400">
                Pour consulter « {meta.doc_name} », veuillez lire et signer électroniquement l'accord de
                confidentialité et de non-contournement (NCNDA) ci-dessous.
              </p>
              <div className="bg-black/30 border border-white/10 rounded p-4 max-h-72 overflow-y-auto whitespace-pre-line text-[11px] leading-relaxed text-zinc-300" data-testid="public-share-nda-text">
                {meta.nda_text}
              </div>
              <form onSubmit={sign} className="space-y-3 bg-white/[0.02] border border-white/10 rounded p-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required minLength={2} placeholder="Nom et prénom *" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-black/30 border border-white/10 rounded px-3 py-2 text-xs text-zinc-100 w-full"
                    data-testid="nda-name" />
                  <input placeholder="Société" value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="bg-black/30 border border-white/10 rounded px-3 py-2 text-xs text-zinc-100 w-full"
                    data-testid="nda-company" />
                </div>
                <input required type="email" placeholder="Email *" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-black/30 border border-white/10 rounded px-3 py-2 text-xs text-zinc-100 w-full"
                  data-testid="nda-email" />
                <label className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer">
                  <input type="checkbox" checked={form.accept} required
                    onChange={(e) => setForm({ ...form, accept: e.target.checked })}
                    className="mt-0.5" data-testid="nda-accept" />
                  <span>Je m'engage et j'accepte les termes de l'accord de confidentialité et de non-contournement ci-dessus. Ma signature électronique (nom, horodatage, IP) sera enregistrée.</span>
                </label>
                <button type="submit" disabled={signing || !form.accept}
                  className="w-full bg-[#1D9E75] hover:bg-[#178a65] disabled:opacity-40 text-white text-xs font-semibold rounded px-4 py-2.5 flex items-center justify-center gap-2 transition-colors"
                  data-testid="nda-sign-btn">
                  {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Signer et accéder au document
                </button>
              </form>
            </div>
          </div>
        )}

        {!loading && !error && canView && (
          <div className="flex-1 flex flex-col min-h-0 p-3 gap-3" data-testid="public-share-viewer">
            <div className="flex items-center justify-end gap-2">
              {meta.require_nda && (
                <span className="text-[10px] text-[#1D9E75] flex items-center gap-1 mr-auto"><ShieldCheck className="w-3.5 h-3.5" /> NDA signé — accès autorisé</span>
              )}
              <a href={fileUrl(true)} className="text-xs text-zinc-300 hover:text-white flex items-center gap-1.5 px-3 py-1.5 rounded border border-white/10 hover:bg-white/5" data-testid="public-share-download">
                <Download className="w-3.5 h-3.5" /> Télécharger
              </a>
            </div>
            {previewable ? (
              <iframe src={fileUrl(false)} title={meta.doc_name} className="flex-1 w-full rounded bg-white" data-testid="public-share-frame" />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-zinc-400 text-sm">
                <FileText className="w-12 h-12 text-[#378ADD] opacity-40" />
                <div>Aperçu non disponible pour ce format — utilisez le téléchargement.</div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
